import { Fragment } from 'react';

import { Text, VSpacing } from '@hh.ru/magritte-ui';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import CollapseCollection from 'src/components/ResumeCard/Body/Collapse/Collection';
import DateInterval from 'src/components/ResumeCard/Body/DateInterval';
import Field from 'src/components/ResumeCard/Body/Field';
import Divider from 'src/components/ResumeCard/Divider';
import HiddenByApplicant from 'src/components/ResumeCard/HiddenByApplicant';
import { getTextStyle } from 'src/components/ResumeCard/utils/getTextStyle';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';
import { HiddenFields } from 'src/models/resume/resumeCommon.types';
import type { ResumeCardData } from 'src/models/resumeCard';
import { SettingType } from 'src/models/search/resume/result';

import LastExperience from 'src/components/ResumeCard/Body/Experience/Last';

export const getShouldShowExperience = (activeSettings: SettingType[]): boolean =>
    activeSettings.some((setting) => [SettingType.LastExperience, SettingType.Companies].includes(setting));

const TrlKeys = {
    experience: 'resumeCard.field.experience',
    laseExperience: 'resumeCard.field.lastExperience',
    hidden: 'resume.hiddenFields.experience',
};

interface Props {
    resume: ResumeCardData;
    isGifted: boolean;
    isNew: boolean;
    markAsRead?: () => void;
    isVisited: boolean;
}

const Experience: TranslatedComponent<Props> = ({ resume, isGifted, isNew, markAsRead, isVisited, trls }) => {
    const activeSettings = useSelector((state) => state.resumeSearchResult.settings.activeSettings);

    const isLastExperienceMode =
        !activeSettings.includes(SettingType.Companies) && activeSettings.includes(SettingType.LastExperience);
    const isExperienceListMode =
        activeSettings.includes(SettingType.Companies) && !activeSettings.includes(SettingType.LastExperience);

    const experienceList =
        (isLastExperienceMode && resume.shortExperience?.slice(0, 1)) || resume.shortExperience || [];

    if (!experienceList?.length) {
        return null;
    }

    const hasHiddenExperience = resume.hiddenFields.some((field) => field.string === HiddenFields.Experience);

    return (
        <Field title={trls[isLastExperienceMode ? TrlKeys.laseExperience : TrlKeys.experience]} dataQaName="experience">
            <CollapseCollection
                collection={experienceList}
                renderItem={({ id, companyName, position, startDate, endDate }, index) => (
                    <Fragment key={id}>
                        {index !== 0 && <VSpacing default={8} />}
                        <div>
                            <div>
                                {hasHiddenExperience ? (
                                    <HiddenByApplicant>{trls[TrlKeys.hidden]}</HiddenByApplicant>
                                ) : (
                                    <Text
                                        Element="span"
                                        typography="subtitle-2-semibold"
                                        style={getTextStyle(isVisited)}
                                    >
                                        {companyName}
                                    </Text>
                                )}
                            </div>
                            {position}
                            <Divider />
                            <DateInterval start={startDate} end={endDate || ''} />
                        </div>
                        {index === 0 && !isExperienceListMode && (
                            <LastExperience
                                lastExperienceId={id}
                                resume={resume}
                                isGifted={isGifted}
                                isNew={isNew}
                                markAsRead={markAsRead}
                            />
                        )}
                    </Fragment>
                )}
                dataQa="experience"
            />
        </Field>
    );
};

export default translation(Experience);
